/**
 * Conversation Search Service — calls the `searchConversations` callable,
 * which grades every one of the user's conversations against a query with
 * a cheap LLM (noragsearch-style selection) and returns a ranked top-N.
 */

import { getFunctions, httpsCallable } from 'firebase/functions';

export type SearchCorpus = 'prompts' | 'full';

export interface ConversationSearchHit {
  conversationId: string;
  title: string;
  grade: string; // A, B, C, D, F, a blend like "B/C", or "—" if ungraded
  rationale?: string;
  lastUpdatedAt: number;
  /** Number of user prompts in the conversation. */
  turns: number;
}

export interface ConversationSearchStats {
  conversations: number;
  candidates: number;
  digestsRebuilt: number;
  binders: number;
  reasonerCalls: number;
  ungraded: number;
  costUSD: number;
  durationMs: number;
  graderModel: string;
  /** The grader's own one-sentence reading of the request (free text). */
  interpretation?: string;
  /** Count of candidates per effective grade (plus "ungraded"). */
  gradeDistribution?: Record<string, number>;
  /** Requested page size (the client pages the full ranked list). */
  pageSize?: number;
  /** Wall-clock per phase: load (Firestore), pack, wave (LLM), retry, rank. */
  phaseMs?: Record<string, number>;
}

export interface ConversationSearchResult {
  hits: ConversationSearchHit[];
  stats: ConversationSearchStats;
  biasWarning?: string;
}

export async function searchConversations(
  query: string,
  maxHits: number,
  corpus: SearchCorpus,
): Promise<ConversationSearchResult> {
  const functions = getFunctions();
  const fn = httpsCallable<
    { query: string; maxHits: number; corpus: SearchCorpus },
    ConversationSearchResult
  >(functions, 'searchConversations');
  const result = await fn({ query, maxHits, corpus });
  return result.data;
}
